import React, { useContext, useEffect, useRef, useState } from "react";
import { Paper, Typography, Box, Avatar, ButtonBase } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CallIcon from "@material-ui/icons/Call";
import CallEndIcon from "@material-ui/icons/CallEnd";
import MicIcon from "@material-ui/icons/Mic";
import MicOffIcon from "@material-ui/icons/MicOff";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import PersonIcon from "@material-ui/icons/Person";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import openSocket from "socket.io-client";
import Draggable from "react-draggable";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import ringSound from "../../assets/skype.mp3";
import { CallAudio } from "./audio";
import { t } from "./messages";

const message = error => error.response?.data?.error || error.message || "Não foi possível controlar a chamada";

const gradients = {
  ringing: "linear-gradient(160deg, #25D366 0%, #128C7E 100%)",
  live: "linear-gradient(160deg, #128C7E 0%, #075E54 100%)",
  idle: "linear-gradient(160deg, #78909C 0%, #455A64 100%)"
};

const useStyles = makeStyles(() => ({
  "@keyframes nativeCallPulse": {
    "0%": { transform: "scale(1)", opacity: 0.55 },
    "100%": { transform: "scale(1.9)", opacity: 0 }
  },
  "@keyframes nativeCallShake": {
    "0%, 60%, 100%": { transform: "rotate(0deg)" },
    "10%, 30%, 50%": { transform: "rotate(-14deg)" },
    "20%, 40%": { transform: "rotate(14deg)" }
  },
  "@keyframes nativeCallIn": {
    from: { transform: "translateY(24px)", opacity: 0 },
    to: { transform: "translateY(0)", opacity: 1 }
  },
  stack: {
    position: "fixed", bottom: 24, right: 24, width: 320, maxWidth: "calc(100vw - 32px)",
    maxHeight: "80vh", overflowY: "auto", zIndex: 1400
  },
  card: {
    marginBottom: 12, borderRadius: 20, overflow: "hidden", color: "#fff",
    boxShadow: "0 12px 32px rgba(0,0,0,0.28)", animation: "$nativeCallIn 0.28s ease-out"
  },
  top: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 16px 6px", fontSize: 12, opacity: 0.9,
    cursor: "grab", touchAction: "none", userSelect: "none"
  },
  brand: { display: "flex", alignItems: "center", gap: 6, minWidth: 0 },
  brandText: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  body: { display: "flex", flexDirection: "column", alignItems: "center", padding: "20px 20px 8px", textAlign: "center" },
  avatarWrap: { position: "relative", width: 76, height: 76, marginBottom: 14 },
  ring: {
    position: "absolute", top: 0, right: 0, bottom: 0, left: 0, borderRadius: "50%", background: "rgba(255,255,255,0.45)",
    animation: "$nativeCallPulse 1.8s ease-out infinite"
  },
  ringDelayed: { animationDelay: "0.9s" },
  avatar: {
    position: "relative", width: 76, height: 76, fontSize: 28, fontWeight: 600,
    color: "#075E54", background: "#fff", boxShadow: "0 4px 14px rgba(0,0,0,0.18)"
  },
  name: { fontWeight: 600, lineHeight: 1.25, wordBreak: "break-word" },
  peer: { opacity: 0.85, fontSize: 13, marginTop: 2, wordBreak: "break-all" },
  status: { marginTop: 6, fontSize: 13, opacity: 0.9, letterSpacing: 0.3 },
  timer: { marginTop: 4, fontSize: 30, fontWeight: 300, fontVariantNumeric: "tabular-nums", letterSpacing: 1 },
  notice: {
    marginTop: 10, padding: "6px 10px", borderRadius: 10, fontSize: 12,
    background: "rgba(0,0,0,0.18)"
  },
  actions: { display: "flex", justifyContent: "space-evenly", padding: "12px 12px 18px" },
  action: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: 6, borderRadius: 12,
    padding: 4, color: "#fff", fontSize: 12, "&.Mui-disabled": { opacity: 0.45 }
  },
  circle: {
    width: 56, height: 56, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 4px 12px rgba(0,0,0,0.25)", transition: "transform 0.15s ease, filter 0.15s ease",
    "$action:hover &": { transform: "scale(1.07)", filter: "brightness(1.08)" }
  },
  accept: { background: "#fff", color: "#128C7E" },
  shake: { animation: "$nativeCallShake 1.4s ease-in-out infinite" },
  reject: { background: "#F44336", color: "#fff" },
  soft: { background: "rgba(255,255,255,0.2)", color: "#fff" },
  softOn: { background: "#fff", color: "#075E54" }
}));

// "5511999998888@s.whatsapp.net" -> "+55 11 99999-8888"; LIDs and other identifiers stay as received.
const formatPeer = peer => {
  const phone = /^(\d+)@s\.whatsapp\.net$/.exec(peer || "")?.[1];
  if (!phone) return peer;
  const br = /^55(\d{2})(\d{4,5})(\d{4})$/.exec(phone);
  return br ? `+55 ${br[1]} ${br[2]}-${br[3]}` : `+${phone}`;
};

const initials = name => (name || "").trim().split(/\s+/).filter(Boolean).slice(0, 2)
  .map(part => part[0].toUpperCase()).join("");

const CallAction = ({ classes, className, icon, label, ...props }) =>
  <ButtonBase className={classes.action} focusRipple {...props}>
    <span className={`${classes.circle} ${className}`}>{icon}</span>
    <span>{label}</span>
  </ButtonBase>;

const connectMedia = async (callId, session, audio) => {
  const { data } = await api.post(`/native-calls/${callId}/media-ticket`, {}, {
    headers: { "X-Call-Session": session }
  });
  const url = new URL(`${api.defaults.baseURL.replace(/\/$/, "")}/native-calls/media`);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.searchParams.set("ticket", data.ticket);
  audio.connect(url.toString());
};

export default function NativeCalls() {
  const classes = useStyles();
  const { isAuth, user } = useContext(AuthContext);
  const [calls, setCalls] = useState([]);
  const [busy, setBusy] = useState(false);
  const [muted, setMuted] = useState(false);
  const [suspended, setSuspended] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [dismissedCallIds, setDismissedCallIds] = useState([]);
  const current = useRef(null);
  const generation = useRef(0);
  const dragNode = useRef(null);

  useEffect(() => {
    if (!isAuth || !user?.id) return;
    setDismissedCallIds([]);
    const activeGeneration = generation.current;
    let unavailableNotified = false;
    let disposed = false;
    const notifyUnavailable = text => {
      if (disposed || unavailableNotified || Number(user.id) !== 1) return;
      unavailableNotified = true;
      toast.error(text, { toastId: "native-calls-unavailable", autoClose: 4000, pauseOnFocusLoss: false });
    };
    const socket = openSocket(api.defaults.baseURL, { auth: { token: localStorage.getItem("socketToken") } });
    socket.on("connect", () => { socket.emit("native-calls:watch"); });
    socket.on("disconnect", () => notifyUnavailable("Avisos de chamadas desconectados. Tentando reconectar…"));
    socket.on("connect_error", () => notifyUnavailable("Não foi possível conectar os avisos de chamadas."));
    socket.on("native-calls:unavailable", () => notifyUnavailable("Consulta de chamadas indisponível. Tentando novamente…"));
    socket.on("native-calls:state", data => {
      setCalls(data.calls);
      setDismissedCallIds(ids => ids.filter(id =>
        data.calls.some(call => call.id === id && !call.endedAt)
      ));
      if (data.unavailable?.length) notifyUnavailable(`Motor de chamadas indisponível: ${data.unavailable.join(", ")}`);
      else unavailableNotified = false;
      const owned = current.current;
      if (owned?.session) {
        const live = data.calls.find(c => c.id === owned.id);
        if (!live || live.endedAt || live.state === "ending") {
          owned.audio.close();
          current.current = null;
          setMuted(false);
          setSuspended(false);
          toast.info(live?.endReason === "remote_absent" ? "Chamada encerrada remotamente." : "Chamada encerrada.");
        }
      }
    });
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      disposed = true;
      generation.current = activeGeneration + 1;
      clearInterval(timer);
      socket.disconnect();
      const owned = current.current;
      current.current = null;
      owned?.audio.close();
      if (owned?.session) api.post(`/native-calls/${owned.id}/hangup`, {},
        { headers: { "X-Call-Session": owned.session } }).catch(() => {});
      setCalls([]);
      setBusy(false);
    };
  }, [isAuth, user?.id]);

  const displayedCalls = calls.filter(call => !dismissedCallIds.includes(call.id));
  const ringing = displayedCalls.some(c => c.state === "ringing" && !c.ownerId && !c.endedAt);
  useEffect(() => {
    if (!ringing || busy || current.current) return;
    const sound = new Audio(ringSound);
    let replayTimer;
    const play = () => {
      sound.currentTime = 0;
      sound.play().catch(() => {}); // The visual notification remains if autoplay is blocked.
    };
    sound.onended = () => {
      replayTimer = setTimeout(play, 2000);
    };
    play();
    return () => {
      clearTimeout(replayTimer);
      sound.onended = null;
      sound.pause();
      sound.currentTime = 0;
    };
  }, [ringing, busy]);

  const hangup = async call => {
    const owned = current.current?.id === call.id ? current.current : null;
    setBusy(true);
    try {
      await api.post(`/native-calls/${call.id}/hangup`, {}, { headers: { "X-Call-Session": owned?.session || "" } });
      owned?.audio.close();
      if (owned) current.current = null;
    } catch (error) { toast.error(message(error)); }
    finally { setBusy(false); }
  };

  const dismiss = call => {
    setDismissedCallIds(ids => ids.includes(call.id) ? ids : [...ids, call.id]);
  };

  useEffect(() => {
    if (!isAuth || !user?.id) return undefined;
    const dial = async event => {
      if (current.current || busy) {
        toast.info("Você já está iniciando ou participando de uma chamada.");
        return;
      }
      const { contactId, whatsappId } = event.detail || {};
      const version = generation.current;
      setBusy(true);
      let audio;
      let session;
      let callId;
      try {
        audio = new CallAudio(() => {
          toast.error("Falha de áudio. Encerrando a chamada.");
          const owned = current.current;
          if (owned?.session) api.post(`/native-calls/${owned.id}/hangup`, {},
            { headers: { "X-Call-Session": owned.session } }).catch(() => {});
          current.current = null;
        }, setSuspended);
        current.current = { audio };
        await audio.prepare();
        if (generation.current !== version) throw new Error("Ligação cancelada");
        const { data } = await api.post("/native-calls/dial", { contactId, whatsappId });
        session = data.session;
        callId = data.call.id;
        if (generation.current !== version || audio.closed) throw new Error("Ligação cancelada");
        current.current = { id: callId, audio, session };
        setMuted(false);
        setCalls(existing => [...existing.filter(call => call.id !== callId), data.call]);
        await connectMedia(callId, session, audio);
      } catch (error) {
        audio?.close();
        if (current.current?.audio === audio) current.current = null;
        if (session && callId) api.post(`/native-calls/${callId}/hangup`, {},
          { headers: { "X-Call-Session": session } }).catch(() => {});
        if (generation.current === version) toast.error(message(error));
      } finally {
        if (generation.current === version) setBusy(false);
      }
    };
    window.addEventListener("native-call:dial", dial);
    return () => window.removeEventListener("native-call:dial", dial);
  }, [busy, isAuth, user?.id]);

  const answer = async call => {
    if (current.current || busy) return;
    const version = generation.current;
    setBusy(true);
    let audio;
    let session;
    try {
      audio = new CallAudio(() => {
        toast.error("Falha de áudio. Encerrando a chamada.");
        const owned = current.current;
        if (owned?.session) api.post(`/native-calls/${owned.id}/hangup`, {},
          { headers: { "X-Call-Session": owned.session } }).catch(() => {});
        current.current = null;
      }, setSuspended);
      current.current = { id: call.id, audio };
      await audio.prepare();
      if (generation.current !== version) throw new Error("Atendimento cancelado");
      const result = await api.post(`/native-calls/${call.id}/answer`);
      session = result.data.session;
      if (generation.current !== version || audio.closed) throw new Error("Atendimento cancelado");
      current.current = { id: call.id, audio, session };
      setMuted(false);
      await connectMedia(call.id, session, audio);
    } catch (error) {
      audio?.close();
      if (current.current?.audio === audio) current.current = null;
      if (session) api.post(`/native-calls/${call.id}/hangup`, {}, { headers: { "X-Call-Session": session } }).catch(() => {});
      if (generation.current === version) toast.error(message(error));
    } finally { if (generation.current === version) setBusy(false); }
  };

  if (!isAuth || !displayedCalls.length) return null;
  return <Draggable nodeRef={dragNode} handle=".native-call-drag-handle" bounds="body">
  <Box ref={dragNode} className={classes.stack} aria-live="polite">
    {displayedCalls.map(call => {
      const owned = current.current?.id === call.id && !!current.current?.session;
      const seconds = call.answeredAt ? Math.max(0, Math.floor(((call.endedAt ? +new Date(call.endedAt) : now) - +new Date(call.answeredAt)) / 1000)) : 0;
      const isRinging = call.state === "ringing" && !call.ownerId && !call.endedAt;
      const isLive = !call.endedAt && ["calling", "answering", "connecting", "active"].includes(call.state);
      const peer = formatPeer(call.peer);
      const letters = initials(call.contactName);
      return <Paper key={call.id} className={classes.card}
        style={{ background: gradients[isRinging ? "ringing" : isLive ? "live" : "idle"] }}>
        <div className={`${classes.top} native-call-drag-handle`}>
          <span className={classes.brand}>
            <WhatsAppIcon style={{ fontSize: 16 }} />
            <span className={classes.brandText}>{call.connectionName}</span>
          </span>
        </div>
        <div className={classes.body}>
          <div className={classes.avatarWrap}>
            {isRinging && <><span className={classes.ring} /><span className={`${classes.ring} ${classes.ringDelayed}`} /></>}
            <Avatar className={classes.avatar}>{letters || <PersonIcon fontSize="large" />}</Avatar>
          </div>
          <Typography variant="h6" className={classes.name}>{call.contactName || peer}</Typography>
          {call.contactName && peer && <Typography className={classes.peer}>{peer}</Typography>}
          {call.answeredAt
            ? <Typography className={classes.timer}>{Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}</Typography>
            : <Typography className={classes.status}>{t(call.state)}</Typography>}
          {call.answeredAt && isLive && <Typography className={classes.status}>{t(call.state)}</Typography>}
          {call.video && <Typography className={classes.notice}>{t("video")}</Typography>}
          {call.ownerId && !owned && !call.endedAt &&
            <Typography className={classes.notice}>{t(call.ownerId === Number(user.id) ? "otherTab" : "otherUser")}</Typography>}
        </div>
        {isRinging && <div className={classes.actions}>
          <CallAction classes={classes} className={classes.reject} icon={<CallEndIcon />} label={t("decline")}
            disabled={busy} onClick={() => dismiss(call)} />
          <CallAction classes={classes} className={`${classes.accept} ${busy || current.current || call.video ? "" : classes.shake}`}
            icon={<CallIcon />} label={t("answer")}
            disabled={busy || !!current.current || call.video} onClick={() => answer(call)} />
        </div>}
        {owned && !call.endedAt && <div className={classes.actions}>
          <CallAction classes={classes} className={muted ? classes.softOn : classes.soft}
            icon={muted ? <MicOffIcon /> : <MicIcon />} label={t(muted ? "unmute" : "mute")}
            onClick={() => { const value = !muted; current.current.audio.mute(value); setMuted(value); }} />
          {suspended && <CallAction classes={classes} className={classes.softOn} icon={<VolumeUpIcon />} label={t("resume")}
            onClick={() => current.current.audio.resume().catch(() => toast.error("Não foi possível ativar o áudio."))} />}
          <CallAction classes={classes} className={classes.reject} icon={<CallEndIcon />} label={t("hangup")}
            disabled={busy} onClick={() => hangup(call)} />
        </div>}
      </Paper>;
    })}
  </Box>
  </Draggable>;
}
